import type { EnergyHistory, Reading } from "@spf/contracts";
import { Card } from "../Card.js";
import { Stat } from "../widgets/Stat.js";
import { CrossCheck } from "./CrossCheck.js";
import { counters } from "../../lib/counters.js";
import { formatUnit } from "../../lib/format.js";
import type { Async } from "../../lib/useAsync.js";

/**
 * The inverter's own counters, today and since it was commissioned, and the
 * check that holds this system's arithmetic to them.
 *
 * The checks sit in the lifetime card because that is what they are: the
 * lifetime-total cross-check of docs/ARCHITECTURE.md section 5, comparing the
 * discharge and the solar energy integrated here with the inverter's own
 * counts over the same stretches of time.
 */
export function Counters({
  reading,
  lifetime,
  onRetry,
}: {
  reading: Reading;
  lifetime: Async<EnergyHistory>;
  onRetry: () => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card title="The inverter's counters today">
        <Stats reading={reading} span="today" />
      </Card>

      <Card title="The inverter's counters since installation">
        <Stats reading={reading} span="total" />
        <div className="mt-6 border-t border-line pt-4">
          <CrossCheck lifetime={lifetime} kinds={["discharge", "solar"]} onRetry={onRetry} />
        </div>
      </Card>
    </div>
  );
}

function Stats({ reading, span }: { reading: Reading; span: "today" | "total" }) {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
      {counters(reading, span).map((counter, index) => (
        <Stat
          key={counter.label}
          label={counter.label}
          value={formatUnit(counter.value, "kWh")}
          tone={index === 0 ? "accent" : "normal"}
        />
      ))}
    </div>
  );
}
